import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { VenuesCollection } from '../models/VenuesCollection'
import { venuesCollectionSchema } from '../models/schema'
import {
  createVenuesCollection as createVenuesCollectionAPI,
  deleteVenuesCollection as deleteVenuesCollectionAPI,
  fetchVenuesCollectionById,
  fetchVenuesCollections,
  updateVenuesCollection as updateVenuesCollectionAPI,
} from '../services/VenuesCollectionService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<VenuesCollection> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'venuesCollection',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getVenuesCollectionsSuccess,
  setQuery: setVenuesCollectionQuery,
  reset: resetVenuesCollectionState,
  resetQueryAndIds: resetVenuesCollectionQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: VenuesCollection }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectVenuesCollections = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedVenuesCollection = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectVenuesCollectionById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectVenuesCollectionsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedVenuesCollection =
  (venuesCollection: VenuesCollection | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<VenuesCollection>(venuesCollection)
      )
    )

export const getVenuesCollections = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchVenuesCollections(params),
    [venuesCollectionSchema],
    slice.actions
  )

export const createVenuesCollection = (venuesCollection: VenuesCollection) =>
  SBAPI.CreateDispatched(
    createVenuesCollectionAPI(venuesCollection),
    venuesCollectionSchema,
    slice.actions
  )

export const getVenuesCollectionWithId = (id: number) =>
  SBAPI.FetchDispatched<VenuesCollection>(
    fetchVenuesCollectionById(id),
    venuesCollectionSchema,
    slice.actions
  )

export const updateVenuesCollection = (venuesCollection: VenuesCollection) =>
  SBAPI.UpdateDispatched<VenuesCollection>(
    updateVenuesCollectionAPI(venuesCollection),
    venuesCollection,
    slice.actions
  )

export const deleteVenuesCollection = (venuesCollection: VenuesCollection) =>
  SBAPI.DestroyDispatched<VenuesCollection>(
    deleteVenuesCollectionAPI(venuesCollection),
    venuesCollection,
    slice.actions
  )
